import React from 'react';

import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  InfiniteApplication,
  InfiniteApplicationApplicant,
  InfiniteApplicationAssociatedEligibility,
  InfiniteApplicationBookingAuthority,
  InfiniteApplicationStatus,
} from '~/services/LuxLoyalty';
import { formatDateDDMonYY } from '~/services/TimeService';

import {
  ApplicantAccountState,
  ApprovedApplicationEnrolmentAction,
  InfiniteApplicationEnrolmentRequest,
} from './ReviewApplicationApplicantActions';
import { HEADER_CELL_SX, getAddress, getDisplayName, getPhone } from './infiniteApplicationReview.utils';

function getNullablePhone(person: Pick<InfiniteApplicationAssociatedEligibility, 'phonePrefix' | 'phone'>): string {
  return [person.phonePrefix, person.phone].filter(Boolean).join(' ');
}

export function ApplicantsTable({
  application,
  applicants,
  applicationStatus,
  onStartEnrolment,
  onAccountStateChange,
}: {
  application: InfiniteApplication;
  applicants: Array<InfiniteApplicationApplicant>;
  applicationStatus: InfiniteApplicationStatus;
  onStartEnrolment: (request: InfiniteApplicationEnrolmentRequest) => void;
  onAccountStateChange: (applicantId: string, state: ApplicantAccountState) => void;
}) {
  const showActions = applicationStatus === 'APPROVED' || applicationStatus === 'FINALIZED';

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={HEADER_CELL_SX}>Role</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Name</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Relationship</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Email</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Phone</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Address</TableCell>
            {showActions && <TableCell sx={HEADER_CELL_SX}>Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {applicants.map((applicant) => (
            <TableRow key={applicant.id}>
              <TableCell>
                <Stack spacing={0.75} alignItems="flex-start">
                  <Typography variant="body2">{applicant.role}</Typography>
                </Stack>
              </TableCell>
              <TableCell>{getDisplayName(applicant)}</TableCell>
              <TableCell>{applicant.relationship || '-'}</TableCell>
              <TableCell>{applicant.email}</TableCell>
              <TableCell>{getPhone(applicant)}</TableCell>
              <TableCell>{getAddress(applicant) || '-'}</TableCell>
              {showActions && (
                <TableCell sx={{ minWidth: 260 }}>
                  <ApprovedApplicationEnrolmentAction
                    application={application}
                    applicant={applicant}
                    onStartEnrolment={onStartEnrolment}
                    onAccountStateChange={onAccountStateChange}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function AssociatedEligibilityTable({ eligibility }: { eligibility: InfiniteApplicationAssociatedEligibility }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={HEADER_CELL_SX}>Name</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Email</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Phone</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Status</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Expires</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{[eligibility.firstName, eligibility.lastName].filter(Boolean).join(' ')}</TableCell>
            <TableCell>{eligibility.email}</TableCell>
            <TableCell>{getNullablePhone(eligibility) || '-'}</TableCell>
            <TableCell>
              <Typography
                variant="body2"
                color={eligibility.isRetracted ? 'error.main' : 'success.main'}
                sx={{ fontWeight: 700 }}
              >
                {eligibility.isRetracted ? 'Retracted' : 'Active'}
              </Typography>
            </TableCell>
            <TableCell>{eligibility.expiresAt ? formatDateDDMonYY(eligibility.expiresAt) : '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function BookingAuthoritiesTable({ authorities }: { authorities: Array<InfiniteApplicationBookingAuthority> }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={HEADER_CELL_SX}>Name</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Email</TableCell>
            <TableCell sx={HEADER_CELL_SX}>Phone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {authorities.length > 0 ? (
            authorities.map((authority) => (
              <TableRow key={authority.id}>
                <TableCell>{getDisplayName(authority)}</TableCell>
                <TableCell>{authority.email}</TableCell>
                <TableCell>{getPhone(authority)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                No booking authorities
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
